/**
 * Generates 2D grid coordinates for workflow steps.
 *
 * Steps that are part of a flow (at least one link in or out) are laid out as a
 * layered DAG: columns are critical-path depth (left to right by dependency),
 * rows are parallel steps at the same depth.
 *
 * Steps with no links ("isolated") aren't part of any flow, so they're packed
 * into a compact band across the top rows instead of stacking full-width, and
 * the connected flow starts in the rows below. The band is DEFAULT_ISOLATED_COLS
 * wide by default, but widens to match a longer connected flow so the standalone
 * steps reuse that same horizontal space instead of clumping to the left.
 * `compareIsolated` orders the band — the caller uses it to give webpage entry
 * points primacy.
 *
 * Example: A->B, B->C, A->C, C->D
 *
 *   col0  col1  col2
 * row0: A    B
 * row1:      C    D
 *
 * @returns Map of stepId to {row, col} coordinates
 */

const DEFAULT_ISOLATED_COLS = 4;

export function layoutWorkflowSteps<T extends { id: string }>(
  items: readonly T[],
  links: readonly { fromStepId: string; toStepId: string }[],
  options?: { compareIsolated?: (a: T, b: T) => number }
): Map<string, { row: number; col: number }> {
  const positions = new Map<string, { row: number; col: number }>();

  if (items.length === 0) {
    return positions;
  }

  // Build adjacency lists
  const incomingEdges = new Map<string, Set<string>>();
  const outgoingEdges = new Map<string, Set<string>>();

  for (const item of items) {
    incomingEdges.set(item.id, new Set());
    outgoingEdges.set(item.id, new Set());
  }

  for (const link of links) {
    incomingEdges.get(link.toStepId)?.add(link.fromStepId);
    outgoingEdges.get(link.fromStepId)?.add(link.toStepId);
  }

  // Steps with no links in or out aren't part of any flow; pack them into a
  // compact top band, then start the connected DAG in the rows below.
  const isolated: T[] = [];
  const connected: T[] = [];
  for (const item of items) {
    const degree =
      incomingEdges.get(item.id)!.size + outgoingEdges.get(item.id)!.size;
    (degree === 0 ? isolated : connected).push(item);
  }

  // Calculate column for each connected step using critical path (longest path
  // from any root). This ensures proper left-to-right ordering by dependency.
  const columns = new Map<string, number>();
  const visiting = new Set<string>(); // Track nodes being visited to detect cycles

  function calculateColumn(id: string): number {
    if (columns.has(id)) {
      return columns.get(id)!;
    }

    // Cycle detection: if we're already visiting this node, we have a cycle
    if (visiting.has(id)) {
      return 0;
    }

    const incoming = incomingEdges.get(id);
    if (!incoming || incoming.size === 0) {
      columns.set(id, 0);
      return 0;
    }

    visiting.add(id);

    let maxPredecessorCol = -1;
    for (const predId of incoming) {
      const predCol = calculateColumn(predId);
      maxPredecessorCol = Math.max(maxPredecessorCol, predCol);
    }

    visiting.delete(id);

    const col = maxPredecessorCol + 1;
    columns.set(id, col);
    return col;
  }

  for (const item of connected) {
    calculateColumn(item.id);
  }

  // Isolated steps default to a compact band, but widen to fill a longer flow so
  // they reuse the same horizontal space rather than clumping left.
  const dagColCount = columns.size > 0 ? Math.max(...columns.values()) + 1 : 0;
  const bandWidth = Math.min(
    isolated.length,
    Math.max(DEFAULT_ISOLATED_COLS, dagColCount)
  );
  const bandRowCount =
    bandWidth > 0 ? Math.ceil(isolated.length / bandWidth) : 0;

  const orderedIsolated = options?.compareIsolated
    ? [...isolated].sort(options.compareIsolated)
    : isolated;

  orderedIsolated.forEach((item, i) => {
    positions.set(item.id, {
      row: Math.floor(i / bandWidth),
      col: i % bandWidth,
    });
  });

  // Group connected items by column
  const columnGroups = new Map<number, string[]>();
  for (const item of connected) {
    const col = columns.get(item.id)!;

    if (!columnGroups.has(col)) {
      columnGroups.set(col, []);
    }
    columnGroups.get(col)!.push(item.id);
  }

  // Assign rows within each column, in DAG-local coordinates (row 0 = first
  // row of the flow); the whole DAG is shifted below the band afterwards.
  const dagPositions = new Map<string, { row: number; col: number }>();

  if (columnGroups.size > 0) {
    const maxCol = Math.max(...Array.from(columnGroups.keys()));

    for (let col = 0; col <= maxCol; col++) {
      const itemsInCol = columnGroups.get(col) || [];

      // For each item in this column, find the first available row
      for (const itemId of itemsInCol) {
        const incoming = incomingEdges.get(itemId)!;

        // Try to reuse the same row as predecessors if possible (for sequential chains)
        // Otherwise, find the first available row
        let targetRow = 0;

        if (incoming.size > 0) {
          // Get all predecessor rows
          const predRows = Array.from(incoming)
            .map((predId) => dagPositions.get(predId)?.row)
            .filter((r): r is number => r !== undefined);

          if (predRows.length > 0) {
            // For single predecessor, try to reuse its row
            // For multiple predecessors, need a new row after the max
            if (predRows.length === 1) {
              targetRow = predRows[0]!;
            } else {
              targetRow = Math.max(...predRows) + 1;
            }
          }
        }

        // Find the first available row at or after targetRow
        const usedRows = new Set(
          Array.from(dagPositions.values())
            .filter((p) => p.col === col)
            .map((p) => p.row)
        );

        let row = targetRow;
        while (usedRows.has(row)) {
          row++;
        }

        dagPositions.set(itemId, { row, col });
      }
    }
  }

  for (const [id, pos] of dagPositions) {
    positions.set(id, { row: pos.row + bandRowCount, col: pos.col });
  }

  return positions;
}
